import React from 'react';
import {Controls, ControlsItem, PageContent, Preloader, Switch, Typography} from '@genestack/ui';
import {Sidebar} from '../../../../components/layout/sidebar';
import {SidebarHeader} from '../../../../components/layout/sidebar/sidebar-header/sidebar-header';
import {useTreeStructureQuery} from './hooks/use-tree-structure-query';
import {getTreeItem, getTreeSidebarTitle} from './helpers';
import {isDocumentBeingEdited} from '../../../../utils/document';
import {useFoldingItemsState} from '../../../../hooks/use-folding-items-state';
import styles from './ontology-tree.module.css';
import {LS_PREFIX} from '../../../../providers/global-state';
import {useLsFlag} from '../../../../hooks/use-ls-flag';
import {OntologyTreeInitialItem} from './ontology-tree-initial-item';

interface Props {
    onClose: () => void;
    docInfo: EditModeDoc;
    foldingItemsProps: ReturnType<typeof useFoldingItemsState>;
    focusNode: (nodeId: string) => void;
    focusedNode?: string;
}

export function OntologyTreeSidebar(props: Props) {
    const isEditedDoc = isDocumentBeingEdited(props.docInfo);
    // we don't want tree to be reloaded every time the focused el changes,
    // that's why we use value from the first render here
    const [firstFocusedNode] = React.useState(props.focusedNode);

    const isAlwaysMultiDoc = props.docInfo.readOnly;
    const isNotionsDoc = props.docInfo.type === 'NOTIONS';
    const [storedIsCurrentDocOnly, setIsCurrentDocOnly] = useLsFlag(
        `${LS_PREFIX}.document.edit.${props.docInfo.accession}.isCurrentDocOnly`,
        !isAlwaysMultiDoc
    );
    // Notions have no hierarchy, so the list always shows the current document only
    const isCurrentDocOnly = isNotionsDoc || storedIsCurrentDocOnly;
    const treeStructureQuery = useTreeStructureQuery({
        accession: props.docInfo.accession,
        isCurrentDocOnly: Boolean(isCurrentDocOnly),
        shouldRefetchFrequently: isEditedDoc,
        anchorEl: firstFocusedNode
    });

    return (
        <Sidebar
            border="left"
            header={
                <SidebarHeader
                    title={getTreeSidebarTitle(props.docInfo.type)}
                    onClose={props.onClose}
                >
                    <Typography
                        as="label"
                        variant="section"
                        intent="quiet"
                        className={styles.onlyThisDocSwitch}
                    >
                        <Controls>
                            <ControlsItem>
                                <Switch
                                    checked={isCurrentDocOnly}
                                    onCheckedChange={setIsCurrentDocOnly}
                                    disabled={isAlwaysMultiDoc || isNotionsDoc}
                                />
                            </ControlsItem>
                            <ControlsItem>Current document only</ControlsItem>{' '}
                        </Controls>
                    </Typography>
                </SidebarHeader>
            }
        >
            {(function () {
                if (!treeStructureQuery.data) {
                    return (
                        <PageContent>
                            <Preloader show count={5} box="paragraph" />
                        </PageContent>
                    );
                }

                return (
                    <div className={styles.root}>
                        {treeStructureQuery.data.rootIds.map((id) => {
                            const element = getTreeItem(treeStructureQuery.data, id);

                            return (
                                <OntologyTreeInitialItem
                                    key={`ontology-item:${JSON.stringify(id)}`}
                                    id={id}
                                    ontologyTreeData={treeStructureQuery.data}
                                    foldingItemsProps={props.foldingItemsProps}
                                    padding={1}
                                    currentDocInfo={props.docInfo}
                                    element={element}
                                    isCurrentDocOnly={isCurrentDocOnly}
                                    focusNode={props.focusNode}
                                    focusedNode={props.focusedNode}
                                />
                            );
                        })}
                    </div>
                );
            })()}
        </Sidebar>
    );
}
